import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { vdAuthApi } from '../api';

export default function VDSettings() {
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [user, setUser] = useState(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const curPwRef = useRef(null);
  const newPwRef = useRef(null);

  useEffect(() => {
    vdAuthApi.me().then(setUser).catch(() => {});
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await vdAuthApi.updateProfile({
        fullName: nameRef.current?.value || '',
        email: emailRef.current?.value || '',
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const cur = curPwRef.current?.value;
    const nw = newPwRef.current?.value;
    if (!cur || !nw) { toast.error('Fill in both password fields'); return; }
    if (nw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      await vdAuthApi.changePassword({ currentPassword: cur, newPassword: nw });
      toast.success('Password changed');
      if (curPwRef.current) curPwRef.current.value = '';
      if (newPwRef.current) newPwRef.current.value = '';
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="vd-settings">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account</p>
      </div>

      <Card className="bg-card/5 border-white/10">
        <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Full Name</Label><Input ref={nameRef} key={user?.name} defaultValue={user?.name || ''} className="bg-card/5 border-white/10 mt-1" data-testid="settings-name" /></div>
            <div><Label>Email</Label><Input ref={emailRef} key={user?.email} defaultValue={user?.email || ''} className="bg-card/5 border-white/10 mt-1" data-testid="settings-email" /></div>
          </div>
          <div><Label>Company</Label><Input value={user?.companyName || ''} className="bg-card/5 border-white/10 mt-1" disabled /></div>
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="save-profile-btn">
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card/5 border-white/10">
        <CardHeader><CardTitle className="text-base">Change Password</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Current Password</Label><Input ref={curPwRef} type="password" className="bg-card/5 border-white/10 mt-1" data-testid="current-password" /></div>
          <div><Label>New Password</Label><Input ref={newPwRef} type="password" className="bg-card/5 border-white/10 mt-1" placeholder="Min 8 characters" data-testid="new-password" /></div>
          <Button onClick={handleChangePassword} disabled={changingPw} variant="outline" className="border-white/10" data-testid="change-password-btn">
            {changingPw ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card/5 border-white/10">
        <CardHeader><CardTitle className="text-base">Billing</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Billing management coming soon. Contact support for plan changes.</p>
        </CardContent>
      </Card>
    </div>
  );
}
